import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Activity,
  Trophy,
  Users,
  Clock,
  Shield,
  Calendar,
  MapPin,
  Star,
  BarChart3,
  Target,
  Award,
  Play,
  Pause,
  Building,
  Zap
} from 'lucide-react';
import { gamesApi } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';

interface GameDetail {
  id: string;
  slug?: string;
  home_team: {
    id: string;
    name: string;
    city: string;
    abbreviation: string;
    slug?: string;
    sport_name: string;
  };
  away_team: {
    id: string;
    name: string;
    city: string;
    abbreviation: string;
    slug?: string;
    sport_name: string;
  };
  home_score: number;
  away_score: number;
  status: 'scheduled' | 'live' | 'final' | 'postponed';
  game_date: string;
  game_time?: string;
  quarter?: string;
  time_remaining?: string;
  championship?: {
    name: string;
    slug: string;
  };
  season?: {
    name: string;
    slug: string;
  };
  venue?: {
    name: string;
    capacity?: number;
    city?: string;
    state?: string;
  };
  weather?: {
    condition: string;
    temperature?: number;
    wind?: string;
  };
  attendance?: number;
  officials?: Array<{
    name: string;
    position: string;
  }>;
}

const ScoreDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const siteConfig = useSiteConfig();
  const [activeTab, setActiveTab] = useState<'overview' | 'stats' | 'plays' | 'info'>('overview');

  // Fetch game details
  const { data: game, isLoading, error } = useQuery<GameDetail>({
    queryKey: ['game', id],
    queryFn: async () => {
      let found;
      try {
        // Try to get by ID first
        const response = await gamesApi.getById(id!);
        found = response.data;
      } catch {
        // Fallback to getAll and search if ID doesn't work
        const response = await gamesApi.getAll();
        const games = response.data;
        found = games.find((g: any) => g.id === id || g.slug === id);
        if (!found) {
          throw new Error('Game not found');
        }
      }
      return {
        id: found.id,
        slug: found.slug,
        home_team: {
          id: found.home_team_id || found.home_team?.id,
          name: found.home_team?.name || found.home_team_name || '',
          city: found.home_team?.city || found.home_team_city || '',
          abbreviation: found.home_team?.abbreviation || found.home_team_abbreviation || '',
          slug: found.home_team?.slug,
          sport_name: found.home_team?.sport?.name || found.home_team?.sport_name || ''
        },
        away_team: {
          id: found.away_team_id || found.away_team?.id,
          name: found.away_team?.name || found.away_team_name || '',
          city: found.away_team?.city || found.away_team_city || '',
          abbreviation: found.away_team?.abbreviation || found.away_team_abbreviation || '',
          slug: found.away_team?.slug,
          sport_name: found.away_team?.sport?.name || found.away_team?.sport_name || ''
        },
        home_score: found.home_score || 0,
        away_score: found.away_score || 0,
        status: found.status || 'scheduled',
        game_date: found.game_date,
        game_time: found.game_time,
        quarter: found.quarter || found.period,
        time_remaining: found.time_remaining,
        championship: found.championship,
        season: found.season,
        venue: found.venue_name ? {
          name: found.venue_name || found.venue?.name,
          capacity: found.venue?.capacity,
          city: found.venue?.city,
          state: found.venue?.state
        } : undefined,
        weather: found.weather,
        attendance: found.attendance,
        officials: found.officials
      };
    },
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-card p-12 text-center max-w-md">
          <Activity className="w-16 h-16 text-error mx-auto mb-4 opacity-50" />
          <h1 className="text-2xl font-bold text-white mb-2">Game Not Found</h1>
          <p className="text-gray-400 mb-6">
            {error ? (error as Error).message : 'The game you are looking for does not exist.'}
          </p>
          <Link
            to="/scores"
            className="btn-primary inline-flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Scores</span>
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'live': return 'text-success';
      case 'final': return 'text-gray-400';
      case 'scheduled': return 'text-primary';
      case 'postponed': return 'text-warning';
      default: return 'text-gray-400';
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'live': return 'badge-success';
      case 'final': return 'badge-secondary';
      case 'scheduled': return 'badge-primary';
      case 'postponed': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  const getWinnerClass = (teamScore: number, opponentScore: number, status: string) => {
    if (status === 'scheduled') return '';
    return teamScore > opponentScore ? 'text-success' : teamScore < opponentScore ? 'text-gray-400' : '';
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Shield },
    { id: 'stats', label: 'Stats', icon: BarChart3 },
    { id: 'plays', label: 'Plays', icon: Activity },
    { id: 'info', label: 'Game Info', icon: Calendar },
  ];

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Breadcrumb
            items={[
              { label: 'All Scores', path: '/scores' },
              { label: game ? `${game.home_team?.name} vs ${game.away_team?.name}` : 'Game Details', isActive: true }
            ]}
          />

          <div className="glass-card p-8">
            {/* Game Status Bar */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-4">
                <span className="badge badge-primary text-xs">
                  {game.home_team.sport_name}
                </span>
                {game.status === 'live' && (
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-success rounded-full animate-pulse" />
                    <span className="text-success font-bold text-sm">LIVE</span>
                    {game.quarter && game.time_remaining && (
                      <span className="text-white text-sm">
                        {game.quarter} - {game.time_remaining}
                      </span>
                    )}
                  </div>
                )}
                {game.status !== 'live' && (
                  <span className={`badge ${getStatusBadge(game.status)} text-xs`}>
                    {game.status === 'scheduled' && game.game_time
                      ? game.game_time
                      : game.status.charAt(0).toUpperCase() + game.status.slice(1)
                    }
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-400">
                {new Date(game.game_date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </div>
            </div>

            {/* Matchup */}
            <div className="space-y-6">
              {/* Away Team */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-surface/30">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-gray-700 to-gray-800 border border-border flex items-center justify-center">
                    <span className="text-lg font-bold text-white">
                      {game.away_team.abbreviation}
                    </span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {game.away_team.name}
                    </div>
                    <div className="text-sm text-gray-400">Away</div>
                  </div>
                </div>
                <div className={`text-5xl font-black ${getWinnerClass(game.away_score, game.home_score, game.status)} ${game.status === 'scheduled' ? 'text-gray-600' : 'text-white'}`}>
                  {game.status === 'scheduled' ? '-' : game.away_score}
                </div>
              </div>

              {/* VS Divider */}
              <div className="flex items-center justify-center">
                <div className="flex items-center space-x-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20">
                  <span className="text-primary font-bold text-sm">VS</span>
                </div>
              </div>

              {/* Home Team */}
              <div className="flex items-center justify-between p-4 rounded-lg bg-surface/30">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/30 flex items-center justify-center">
                    <span className="text-lg font-bold text-primary">
                      {game.home_team.abbreviation}
                    </span>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">
                      {game.home_team.name}
                    </div>
                    <div className="text-sm text-gray-400">Home</div>
                  </div>
                </div>
                <div className={`text-5xl font-black ${getWinnerClass(game.home_score, game.away_score, game.status)} ${game.status === 'scheduled' ? 'text-gray-600' : 'text-white'}`}>
                  {game.status === 'scheduled' ? '-' : game.home_score}
                </div>
              </div>
            </div>

            {/* Game Context */}
            <div className="mt-6 flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {game.championship && (
                  <div className="flex items-center space-x-2">
                    <Star className="w-4 h-4 text-primary" />
                    <span className="text-primary font-semibold text-sm">
                      {game.championship.name}
                    </span>
                  </div>
                )}
                {game.venue && (
                  <div className="flex items-center space-x-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-400 text-sm">
                      {game.venue.name}
                    </span>
                  </div>
                )}
              </div>
              {game.attendance && (
                <div className="text-sm text-gray-400">
                  Attendance: {game.attendance.toLocaleString()}
                </div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="glass-card p-1 mb-8">
          <div className="flex space-x-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg
                    transition-all duration-200
                    ${activeTab === tab.id
                      ? 'bg-primary/20 text-primary border border-primary/30'
                      : 'text-gray-400 hover:text-white hover:bg-surface/50'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Game Summary */}
              <div className="lg:col-span-2 space-y-6">
                <div className="glass-card p-6">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                    <Activity className="w-5 h-5 mr-2 text-primary" />
                    Game Summary
                  </h2>
                  
                  {game.status === 'final' && (
                    <div className="p-4 rounded-lg bg-primary/10 border border-primary/20 mb-4">
                      <div className="text-primary font-semibold text-sm uppercase tracking-wider mb-1">
                        Final Result
                      </div>
                      <div className="text-2xl font-bold text-white">
                        {game.away_score > game.home_score 
                          ? `${game.away_team.name} defeats ${game.home_team.name}`
                          : game.home_score > game.away_score
                          ? `${game.home_team.name} defeats ${game.away_team.name}`
                          : 'Game ended in a tie'
                        }
                      </div>
                      <div className="text-lg text-gray-300 mt-1">
                        {game.away_score} - {game.home_score}
                      </div>
                    </div>
                  )}

                  {game.status === 'scheduled' && (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-primary mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-white mb-2">Game Preview</h3>
                      <p className="text-gray-400">
                        Game summary will be available after the game starts.
                      </p>
                    </div>
                  )}

                  {game.status === 'live' && (
                    <div className="text-center py-8">
                      <Zap className="w-12 h-12 text-success mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-white mb-2">Game In Progress</h3>
                      <p className="text-gray-400">
                        Live game updates coming soon.
                      </p>
                    </div>
                  )}

                  {game.status === 'final' && (
                    <div className="text-center py-8">
                      <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                      <h3 className="text-lg font-bold text-white mb-2">Game Recap</h3>
                      <p className="text-gray-400">
                        Detailed game recap coming soon.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Game Info */}
                <div className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4">
                    Game Details
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Status</span>
                      <span className={`badge ${getStatusBadge(game.status)}`}>
                        {game.status.charAt(0).toUpperCase() + game.status.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-400">Date</span>
                      <span className="text-white font-semibold">
                        {new Date(game.game_date).toLocaleDateString()}
                      </span>
                    </div>
                    {game.game_time && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Time</span>
                        <span className="text-white font-semibold">
                          {game.game_time}
                        </span>
                      </div>
                    )}
                    {game.season && (
                      <div className="flex items-center justify-between">
                        <span className="text-gray-400">Season</span>
                        <span className="text-white font-semibold">
                          {game.season.name}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Venue Info */}
                {game.venue && (
                  <div className="glass-card p-6">
                    <h3 className="text-lg font-bold text-white mb-4 flex items-center">
                      <Building className="w-5 h-5 mr-2 text-primary" />
                      Venue
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Name</div>
                        <div className="text-white font-semibold">
                          {game.venue.name}
                        </div>
                      </div>
                      {game.venue.city && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Location</div>
                          <div className="text-white">
                            {game.venue.city}{game.venue.state && `, ${game.venue.state}`}
                          </div>
                        </div>
                      )}
                      {game.venue.capacity && (
                        <div>
                          <div className="text-sm text-gray-400 mb-1">Capacity</div>
                          <div className="text-white">
                            {game.venue.capacity.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'stats' && (
            <motion.div
              key="stats"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <div className="text-center py-12">
                <BarChart3 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Stats Coming Soon</h3>
                <p className="text-gray-400">
                  Game statistics will be available soon.
                </p>
              </div>
            </motion.div>
          )}

          {activeTab === 'plays' && (
            <motion.div
              key="plays"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <div className="text-center py-12">
                <Activity className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Play-by-Play Coming Soon</h3>
                <p className="text-gray-400">
                  Live play-by-play updates will be available soon.
                </p>
              </div>
            </motion.div>
          )}

          {activeTab === 'info' && (
            <motion.div
              key="info"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="glass-card p-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-white mb-4">
                    Game Information
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <div className="text-sm text-gray-400 mb-1">Game ID</div>
                      <div className="text-white font-mono">{game.id}</div>
                    </div>
                    {game.championship && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Championship</div>
                        <div className="text-white">{game.championship.name}</div>
                      </div>
                    )}
                    {game.season && (
                      <div>
                        <div className="text-sm text-gray-400 mb-1">Season</div>
                        <div className="text-white">{game.season.name}</div>
                      </div>
                    )}
                  </div>
                </div>

                {game.officials && game.officials.length > 0 && (
                  <div>
                    <h3 className="text-lg font-bold text-white mb-4">
                      Officials
                    </h3>
                    <div className="space-y-2">
                      {game.officials.map((official, index) => (
                        <div key={index} className="flex justify-between">
                          <span className="text-gray-400">{official.position}</span>
                          <span className="text-white">{official.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ScoreDetail;